import { MdlDialogReference, MdlDialogService } from '@angular-mdl/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, InjectionToken } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { maxBytesError, maxLengthInBytes } from '../../../../shared/validators';
import { AdminEQueryEscalationsStorage } from '../../admin-e-query-escalations-storage';
import { AdminEQueryEscalation } from '../../models/admin-e-query-escalation';

export const DELETE_ESCALATION_TOKEN = new InjectionToken('DeleteEscalation');

@Component({
    selector: 'delete-escalation-dialog',
    template: require('./delete-escalation-dialog.component.html'),
    styles: [require('./delete-escalation-dialog.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeleteEscalationDialogComponent {
    public readonly reasonMaxLength = 250;
    public readonly formGroup: FormGroup;
    public readonly reasonControl = new FormControl('', [Validators.required, maxLengthInBytes(this.reasonMaxLength)]);
    public readonly errors$ = new BehaviorSubject<string[]>(null);

    constructor(
        private readonly dialog: MdlDialogReference,
        @Inject(DELETE_ESCALATION_TOKEN) private readonly escalation: AdminEQueryEscalation,
        private readonly adminEscalationsStorage: AdminEQueryEscalationsStorage,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly dialogService: MdlDialogService,
    ) {
        this.formGroup = new FormGroup({
            'reason': this.reasonControl
        });
    }

    public get errorMessage() {
        if (this.reasonControl.hasError('required')) {
            return 'Reason is required';
        }
        if (this.reasonControl.hasError(maxBytesError)) {
            return `Maximum length is ${this.reasonMaxLength}`;
        }
        return '';
    }

    public async onRemove() {
        if (this.formGroup.invalid) {
            return;
        }

        const confirmed = await this.showConfirm(`The ${this.escalation.email} will be removed from the escalation list. Proceed?`);

        if (!confirmed) {
            return;
        }

        this.formGroup.disable();
        this.changeDetectorRef.markForCheck();

        try {
            const reason: string = this.reasonControl.value;
            const response = await this.adminEscalationsStorage.deleteEscalation(this.escalation, reason);

            if (response.errors.length) {
                this.errors$.next(response.errors);
            } else {
                this.adminEscalationsStorage.reloadEscalations();
                this.dialog.hide();
            }
        } finally {
            this.formGroup.enable();
            this.changeDetectorRef.markForCheck();
        }
    }

    public onCancel() {
        this.dialog.hide();
    }

    private async showConfirm(confirmationText: string): Promise<boolean> {
        try {
            await this.dialogService.confirm(confirmationText, 'No', 'Yes').toPromise();
            return true;
        } catch {
            return false;
        }
    }
}
